// ============================================================================
// BP-13-02: User Detail Panel Component
// BrightPath Platform - Admin Frontend
// ============================================================================

import React, { useState } from 'react';
import { cn } from '@/core/lib/utils';
import type { AdminUser, SuspensionReason } from '@/types/admin';
import {
  useSuspendUser,
  useActivateUser,
  useDeleteUser,
  useResetUserPassword,
  useImpersonateUser,
} from '@/features/admin/hooks/useAdminUsers';

// =============================================================================
// Types
// =============================================================================

interface UserDetailPanelProps {
  user: AdminUser;
  onClose?: () => void;
}

// =============================================================================
// Status Badge
// =============================================================================

function StatusBadge({ status }: { status: AdminUser['status'] }) {
  const statusConfig = {
    active: { label: 'Active', className: 'bg-emerald-100 text-emerald-700' },
    suspended: { label: 'Suspended', className: 'bg-red-100 text-red-700' },
    pending_verification: { label: 'Pending', className: 'bg-amber-100 text-amber-700' },
    deactivated: { label: 'Deactivated', className: 'bg-gray-100 text-gray-600' },
  };

  const config = statusConfig[status];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Role Badge
// =============================================================================

function RoleBadge({ role }: { role: AdminUser['role'] }) {
  const roleConfig = {
    family_admin: { label: 'Family Admin', className: 'bg-sage/10 text-sage-dark' },
    family_member: { label: 'Family Member', className: 'bg-sage/10 text-sage-dark' },
    caregiver: { label: 'Caregiver', className: 'bg-purple-100 text-purple-700' },
    attorney: { label: 'Attorney', className: 'bg-navy/10 text-navy' },
    vendor: { label: 'Vendor', className: 'bg-terracotta/10 text-terracotta' },
    provider: { label: 'Provider', className: 'bg-blue-100 text-blue-700' },
    admin: { label: 'Admin', className: 'bg-amber-100 text-amber-700' },
    super_admin: { label: 'Super Admin', className: 'bg-red-100 text-red-700' },
  };

  const config = roleConfig[role];

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full px-3 py-1 text-sm font-medium',
        config.className
      )}
    >
      {config.label}
    </span>
  );
}

// =============================================================================
// Info Row
// =============================================================================

function InfoRow({ label, value, className }: { label: string; value: React.ReactNode; className?: string }) {
  return (
    <div className={cn('flex items-start justify-between py-3', className)}>
      <span className="text-sm text-warm-gray">{label}</span>
      <span className="text-sm font-medium text-navy text-right">{value || '—'}</span>
    </div>
  );
}

// =============================================================================
// Suspend Modal
// =============================================================================

interface SuspendModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (reason: SuspensionReason, notes?: string) => void;
  isPending: boolean;
}

function SuspendModal({ isOpen, onClose, onConfirm, isPending }: SuspendModalProps) {
  const [reason, setReason] = useState<SuspensionReason>('terms_violation');
  const [notes, setNotes] = useState('');

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h3 className="mb-4 text-lg font-semibold text-navy">Suspend User</h3>
        
        <div className="mb-4">
          <label className="mb-1 block text-sm font-medium text-navy">Reason</label>
          <select
            value={reason}
            onChange={(e) => setReason(e.target.value as SuspensionReason)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
          >
            <option value="terms_violation">Terms Violation</option>
            <option value="spam">Spam</option>
            <option value="inappropriate_content">Inappropriate Content</option>
            <option value="fraud">Fraud</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="mb-6">
          <label className="mb-1 block text-sm font-medium text-navy">Notes (optional)</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/20"
            placeholder="Additional notes about this suspension..."
          />
        </div>

        <div className="flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={isPending}
            className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-navy hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason, notes || undefined)}
            disabled={isPending}
            className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
          >
            {isPending ? 'Suspending...' : 'Suspend User'}
          </button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Component
// =============================================================================

export function UserDetailPanel({ user, onClose }: UserDetailPanelProps) {
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const suspendUser = useSuspendUser();
  const activateUser = useActivateUser();
  const deleteUser = useDeleteUser();
  const resetPassword = useResetUserPassword();
  const impersonateUser = useImpersonateUser();

  const handleSuspend = (reason: SuspensionReason, notes?: string) => {
    suspendUser.mutate(
      { userId: user.id, reason, notes },
      { onSuccess: () => setShowSuspendModal(false) }
    );
  };

  const handleActivate = () => {
    activateUser.mutate(user.id);
  };

  const handleDelete = () => {
    deleteUser.mutate(user.id, {
      onSuccess: () => {
        setShowDeleteConfirm(false);
        onClose?.();
      },
    });
  };

  const handleResetPassword = () => {
    if (confirm('Send password reset email to this user?')) {
      resetPassword.mutate(user.id);
    }
  };

  const handleImpersonate = () => {
    if (confirm('You will be logged in as this user. Continue?')) {
      impersonateUser.mutate(user.id);
    }
  };

  const formatDate = (date: string | null) => {
    if (!date) return null;
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="h-full overflow-y-auto bg-white">
      {/* Header */}
      <div className="sticky top-0 z-10 border-b border-gray-100 bg-white px-6 py-4">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-xl font-semibold text-navy">User Details</h2>
          {onClose && (
            <button
              onClick={onClose}
              className="rounded-lg p-2 text-warm-gray hover:bg-gray-100"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* User Profile */}
      <div className="border-b border-gray-100 px-6 py-6">
        <div className="flex items-center gap-4">
          {user.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.fullName}
              className="h-16 w-16 rounded-full object-cover"
            />
          ) : (
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-sage/10 text-xl font-semibold text-sage-dark">
              {user.fullName.charAt(0)}
            </div>
          )}
          <div>
            <h3 className="text-lg font-semibold text-navy">{user.fullName}</h3>
            <p className="text-sm text-warm-gray">{user.email}</p>
            <div className="mt-2 flex items-center gap-2">
              <StatusBadge status={user.status} />
              <RoleBadge role={user.role} />
            </div>
          </div>
        </div>
      </div>

      {/* Account Info */}
      <div className="border-b border-gray-100 px-6 py-4">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-warm-gray">
          Account Information
        </h4>
        <div className="divide-y divide-gray-50">
          <InfoRow label="User ID" value={<code className="text-xs bg-gray-100 px-2 py-1 rounded">{user.id}</code>} />
          <InfoRow
            label="Email Verified"
            value={
              <span className={user.emailVerified ? 'text-emerald-600' : 'text-amber-600'}>
                {user.emailVerified ? 'Yes' : 'No'}
              </span>
            }
          />
          <InfoRow label="Phone" value={user.phoneNumber} />
          <InfoRow
            label="Phone Verified"
            value={
              <span className={user.phoneVerified ? 'text-emerald-600' : 'text-warm-gray'}>
                {user.phoneVerified ? 'Yes' : 'No'}
              </span>
            }
          />
          <InfoRow
            label="MFA Enabled"
            value={
              <span className={user.mfaEnabled ? 'text-emerald-600' : 'text-warm-gray'}>
                {user.mfaEnabled ? 'Yes' : 'No'}
              </span>
            }
          />
        </div>
      </div>

      {/* Activity Info */}
      <div className="border-b border-gray-100 px-6 py-4">
        <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-warm-gray">
          Activity
        </h4>
        <div className="divide-y divide-gray-50">
          <InfoRow label="Last Login" value={formatDate(user.lastLoginAt)} />
          <InfoRow label="Last Active" value={formatDate(user.lastActiveAt)} />
          <InfoRow label="Login Count" value={user.loginCount.toLocaleString()} />
          <InfoRow label="Failed Logins" value={user.failedLoginAttempts} />
          <InfoRow label="Created" value={formatDate(user.createdAt)} />
        </div>
      </div>

      {/* Suspension Info (if applicable) */}
      {user.status === 'suspended' && (
        <div className="border-b border-gray-100 bg-red-50 px-6 py-4">
          <h4 className="mb-2 text-sm font-semibold uppercase tracking-wide text-red-700">
            Suspension Details
          </h4>
          <div className="divide-y divide-red-100">
            <InfoRow
              label="Suspended At"
              value={formatDate(user.suspendedAt)}
              className="text-red-800"
            />
            <InfoRow
              label="Reason"
              value={user.suspensionReason?.replace('_', ' ')}
              className="text-red-800"
            />
            {user.suspensionNotes && (
              <div className="py-3">
                <span className="block text-sm text-red-700">Notes</span>
                <p className="mt-1 text-sm text-red-800">{user.suspensionNotes}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="px-6 py-6">
        <h4 className="mb-4 text-sm font-semibold uppercase tracking-wide text-warm-gray">
          Actions
        </h4>
        <div className="space-y-3">
          <button
            onClick={handleResetPassword}
            disabled={resetPassword.isPending}
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-navy hover:bg-gray-50 disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
            </svg>
            {resetPassword.isPending ? 'Sending...' : 'Send Password Reset'}
          </button>

          {user.role !== 'super_admin' && (
            <button
              onClick={handleImpersonate}
              disabled={impersonateUser.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-navy hover:bg-gray-50 disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
              {impersonateUser.isPending ? 'Loading...' : 'Impersonate User'}
            </button>
          )}

          {user.status === 'suspended' ? (
            <button
              onClick={handleActivate}
              disabled={activateUser.isPending}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {activateUser.isPending ? 'Activating...' : 'Reactivate User'}
            </button>
          ) : (
            <button
              onClick={() => setShowSuspendModal(true)}
              disabled={user.role === 'super_admin'}
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
              </svg>
              Suspend User
            </button>
          )}

          <button
            onClick={() => setShowDeleteConfirm(true)}
            disabled={user.role === 'super_admin'}
            className="flex w-full items-center justify-center gap-2 rounded-lg bg-red-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
            Delete User
          </button>
        </div>
      </div>

      {/* Suspend Modal */}
      <SuspendModal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        onConfirm={handleSuspend}
        isPending={suspendUser.isPending}
      />

      {/* Delete Confirm */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowDeleteConfirm(false)} />
          <div className="relative z-10 w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
            <h3 className="mb-2 text-lg font-semibold text-navy">Delete User</h3>
            <p className="mb-6 text-warm-gray">
              Are you sure you want to permanently delete this user? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-gray-200 px-4 py-2 text-sm font-medium text-navy hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteUser.isPending}
                className="rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {deleteUser.isPending ? 'Deleting...' : 'Delete User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetailPanel;
